// Create light gradient colors based on selected brand color
export const createLightGradient = (hexColor: string) => {
  // Convert hex to RGB
  const hex = hexColor.replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Create very light versions (mix with white)
  const lightColor1 = `rgb(${Math.floor(r + (255 - r) * 0.9)}, ${Math.floor(g + (255 - g) * 0.9)}, ${Math.floor(b + (255 - b) * 0.9)})`;
  const lightColor2 = `rgb(${Math.floor(r + (255 - r) * 0.85)}, ${Math.floor(g + (255 - g) * 0.85)}, ${Math.floor(b + (255 - b) * 0.85)})`;

  return {
    background: `linear-gradient(to bottom right, ${lightColor1}, ${lightColor2})`,
  };
};

// Class suggestions based on gym type and subtype
// Each subtype includes suggested class names that would be relevant for that type of gym
export const CLASS_SUGGESTIONS = {
  "Functional Fitness": {
    "CrossFit": [
      "WOD (Workout of the Day)",
      "CrossFit Foundations",
      "Open Gym",
      "CrossFit Competition Prep",
      "Olympic Lifting",
      "Strength & Conditioning"
    ],
    "Circuit Training": [
      "Circuit Training",
      "HIIT Circuit",
      "Strength Circuit",
      "Cardio Circuit",
      "Full Body Circuit",
      "Metabolic Circuit"
    ],
    "Indoor Bootcamp": [
      "Bootcamp",
      "HIIT Bootcamp",
      "Strength Bootcamp",
      "Cardio Bootcamp",
      "Total Body Bootcamp",
      "Warrior Bootcamp"
    ],
    "Outdoor Bootcamp": [
      "Outdoor Bootcamp",
      "Beach Bootcamp",
      "Park Workout",
      "Outdoor HIIT",
      "Trail Bootcamp",
      "Fresh Air Fitness"
    ],
    "Kettlebell Training": [
      "Kettlebell Basics",
      "Kettlebell Flow",
      "Kettlebell HIIT",
      "Kettlebell Strength",
      "Russian Kettlebell",
      "Kettlebell Cardio"
    ],
    "TRX Training": [
      "TRX Suspension",
      "TRX Basics",
      "TRX HIIT",
      "TRX Strength",
      "TRX Core",
      "Total Body TRX"
    ],
    "Battle Ropes": [
      "Battle Ropes",
      "Rope Training",
      "HIIT Ropes",
      "Cardio Ropes",
      "Strength & Ropes",
      "Rope Flow"
    ],
    "Plyometrics": [
      "Plyometrics",
      "Jump Training",
      "Explosive Power",
      "Athletic Performance",
      "Speed & Agility",
      "Plyo HIIT"
    ],
    "Calisthenics": [
      "Calisthenics",
      "Bodyweight Training",
      "Street Workout",
      "Gymnastics Strength",
      "Movement Flow",
      "Calisthenics Basics"
    ],
    "Olympic Weightlifting": [
      "Olympic Lifting",
      "Snatch & Clean",
      "Weightlifting Technique",
      "Oly Lifting Basics",
      "Competition Prep",
      "Barbell Technique"
    ],
    "Strongman": [
      "Strongman Training",
      "Atlas Stones",
      "Tire Flips",
      "Farmer's Walk",
      "Strongman Basics",
      "Competition Strongman"
    ],
    "Ninja Warrior Training": [
      "Ninja Training",
      "Obstacle Course",
      "Ninja Basics",
      "Kids Ninja",
      "Adult Ninja",
      "Ninja Conditioning"
    ],
    "Strength and Conditioning": [
      "Strength & Conditioning",
      "Athletic Performance",
      "Sports Conditioning",
      "Functional Strength",
      "Performance Training",
      "S&C Basics"
    ]
  },

  "Strength": {
    "Weightlifting": [
      "Weightlifting",
      "Barbell Training",
      "Strength Training",
      "Heavy Lifting",
      "Powerbuilding",
      "Compound Movements"
    ],
    "Powerlifting": [
      "Powerlifting",
      "Squat, Bench, Deadlift",
      "Competition Prep",
      "Max Effort",
      "Powerlifting Technique",
      "Meet Prep"
    ],
    "Bodybuilding": [
      "Bodybuilding",
      "Muscle Building",
      "Hypertrophy Training",
      "Physique Training",
      "Contest Prep",
      "Pump Training"
    ]
  },

  "Combat Sports": {
    "Boxing": [
      "Boxing",
      "Boxing Technique",
      "Heavy Bag",
      "Sparring",
      "Boxing Conditioning",
      "Beginner Boxing"
    ],
    "Kickboxing": [
      "Kickboxing",
      "Muay Thai Kickboxing",
      "Cardio Kickboxing",
      "Kickboxing Technique",
      "Pad Work",
      "Kickboxing Conditioning"
    ],
    "Muay Thai": [
      "Muay Thai",
      "Thai Boxing",
      "Clinch Work",
      "Pad Training",
      "Muay Thai Conditioning",
      "Traditional Muay Thai"
    ],
    "Brazilian Jiu-Jitsu": [
      "Brazilian Jiu-Jitsu",
      "BJJ Fundamentals",
      "No-Gi BJJ",
      "Gi Training",
      "Rolling Session",
      "BJJ Technique"
    ],
    "Taekwondo": [
      "Taekwondo",
      "TKD Basics",
      "Forms (Poomsae)",
      "Sparring",
      "Self-Defense",
      "Traditional TKD"
    ],
    "Karate": [
      "Karate",
      "Karate Basics",
      "Kata Practice",
      "Kumite",
      "Traditional Karate",
      "Self-Defense"
    ],
    "Combat Sports": [
      "MMA Training",
      "Combat Conditioning",
      "Fight Training",
      "Grappling",
      "Striking",
      "Self-Defense"
    ]
  },

  "Group Fitness": {
    "Zumba": [
      "Zumba",
      "Zumba Gold",
      "Aqua Zumba",
      "Zumba Toning",
      "Dance Fitness",
      "Latin Dance"
    ],
    "Spinning": [
      "Spinning",
      "Indoor Cycling",
      "Cycle Class",
      "HIIT Cycling",
      "Endurance Ride",
      "Climb Ride"
    ],
    "Barre": [
      "Barre",
      "Ballet Barre",
      "Cardio Barre",
      "Strength Barre",
      "Pure Barre",
      "Barre Fusion"
    ],
    "Dance Classes": [
      "Dance Fitness",
      "Hip Hop Dance",
      "Contemporary Dance",
      "Ballroom Dance",
      "Line Dancing",
      "Dance Cardio"
    ],
    "Group Circuit Training": [
      "Group Circuit",
      "Fitness Circuit",
      "Team Training",
      "Circuit Challenge",
      "Group HIIT",
      "Functional Circuit"
    ],
    "Group Classes": [
      "Group Fitness",
      "Team Workout",
      "Fitness Class",
      "All Levels Fitness",
      "Group Training",
      "Community Workout"
    ]
  },

  "Flexibility and Mobility": {
    "Yoga": [
      "Hatha Yoga",
      "Vinyasa Flow",
      "Yin Yoga",
      "Power Yoga",
      "Restorative Yoga",
      "Hot Yoga"
    ],
    "Pilates": [
      "Pilates",
      "Mat Pilates",
      "Reformer Pilates",
      "Classical Pilates",
      "Contemporary Pilates",
      "Pilates Fusion"
    ],
    "Stretching": [
      "Stretch Class",
      "Deep Stretch",
      "Active Stretching",
      "Flexibility Training",
      "Recovery Stretch",
      "Morning Stretch"
    ],
    "Foam Rolling": [
      "Foam Rolling",
      "Self Myofascial Release",
      "Recovery Session",
      "Mobility & Recovery",
      "Trigger Point Therapy",
      "Roll & Stretch"
    ],
    "Tai Chi": [
      "Tai Chi",
      "Moving Meditation",
      "Gentle Movement",
      "Energy Flow",
      "Balance & Harmony",
      "Mindful Movement"
    ]
  },

  "Mind Body": {
    "Meditation": [
      "Meditation",
      "Mindfulness Meditation",
      "Guided Meditation",
      "Silent Meditation",
      "Walking Meditation",
      "Breathwork & Meditation"
    ],
    "Mindful Breathing": [
      "Breathwork",
      "Pranayama",
      "Breathing Techniques",
      "Stress Relief Breathing",
      "Energy Breathing",
      "Mindful Breathing"
    ],
    "Relaxation Techniques": [
      "Relaxation Class",
      "Stress Relief",
      "Progressive Relaxation",
      "Body Scan",
      "Deep Relaxation",
      "Tension Release"
    ],
    "Qi Gong": [
      "Qi Gong",
      "Energy Cultivation",
      "Moving Meditation",
      "Traditional Qi Gong",
      "Healing Qi Gong",
      "Five Element Qi Gong"
    ],
    "Pranayama": [
      "Pranayama",
      "Yogic Breathing",
      "Advanced Breathwork",
      "Energy Breathing",
      "Breathing Meditation",
      "Breath Control"
    ],
    "Sauna": [
      "Sauna Session",
      "Hot/Cold Therapy",
      "Recovery Sauna",
      "Infrared Sauna",
      "Wellness Session",
      "Heat Therapy"
    ]
  },

  "Outdoor Activities": {
    "Hiking": [
      "Group Hiking",
      "Nature Hike",
      "Trail Adventure",
      "Hiking Club",
      "Mountain Hiking",
      "Fitness Hike"
    ],
    "Trail Running": [
      "Trail Running",
      "Off-Road Running",
      "Nature Run",
      "Hill Running",
      "Trail Training",
      "Adventure Run"
    ],
    "Outdoor Cycling": [
      "Group Ride",
      "Road Cycling",
      "Mountain Biking",
      "Cycling Club",
      "Bike Tour",
      "Endurance Ride"
    ],
    "Kayaking": [
      "Kayaking",
      "Paddling Class",
      "Water Adventure",
      "Kayak Training",
      "River Kayaking",
      "Sea Kayaking"
    ],
    "Rock Climbing": [
      "Rock Climbing",
      "Bouldering",
      "Sport Climbing",
      "Climbing Technique",
      "Outdoor Climbing",
      "Climbing Basics"
    ],
    "Surfing": [
      "Surf Lessons",
      "Beginner Surfing",
      "Surf Training",
      "Wave Riding",
      "Surf Fitness",
      "Ocean Safety"
    ],
    "Disc Golfing": [
      "Disc Golf",
      "Frisbee Golf",
      "Disc Golf League",
      "Throwing Clinic",
      "Disc Golf Basics",
      "Tournament Prep"
    ],
    "Stand-up Paddleboarding": [
      "SUP Class",
      "Paddleboard Yoga",
      "SUP Fitness",
      "Beginner SUP",
      "Advanced SUP",
      "Water Balance"
    ]
  },

  "Sports Specific Training": {
    "Basketball Training": [
      "Basketball Skills",
      "Basketball Conditioning",
      "Shooting Clinic",
      "Basketball Drills",
      "Youth Basketball",
      "Basketball Fundamentals"
    ],
    "Track & Field": [
      "Track Training",
      "Sprint Training",
      "Distance Running",
      "Field Events",
      "Track Technique",
      "Athletic Development"
    ],
    "Football Training": [
      "Football Conditioning",
      "Speed & Agility",
      "Football Skills",
      "Strength for Football",
      "Position Training",
      "Off-Season Training"
    ],
    "Soccer Training": [
      "Soccer Skills",
      "Soccer Conditioning",
      "Technical Training",
      "Soccer Fitness",
      "Youth Soccer",
      "Tactical Training"
    ],
    "Golf": [
      "Golf Fitness",
      "Golf Conditioning",
      "Swing Training",
      "Golf Mobility",
      "Core for Golf",
      "Golf Performance"
    ],
    "Softball Training": [
      "Softball Skills",
      "Batting Practice",
      "Fielding Drills",
      "Softball Conditioning",
      "Pitching Clinic",
      "Team Training"
    ],
    "Baseball Training": [
      "Baseball Skills",
      "Hitting Clinic",
      "Pitching Training",
      "Baseball Conditioning",
      "Fielding Practice",
      "Youth Baseball"
    ],
    "Hockey Training": [
      "Hockey Conditioning",
      "Off-Ice Training",
      "Hockey Skills",
      "Power Skating",
      "Hockey Fitness",
      "Stick Handling"
    ],
    "Speed School": [
      "Speed Training",
      "Agility Training",
      "Athletic Speed",
      "Sprint Mechanics",
      "Acceleration Training",
      "Performance Speed"
    ],
    "Sports Performance": [
      "Athletic Performance",
      "Sports Conditioning",
      "Performance Training",
      "Athlete Development",
      "Sport-Specific Training",
      "Elite Performance"
    ]
  },

  "Cardio": {
    "Treadmill": [
      "Treadmill Training",
      "Running Class",
      "Interval Running",
      "Hill Training",
      "Endurance Run",
      "Speed Work"
    ],
    "Elliptical": [
      "Elliptical Training",
      "Low Impact Cardio",
      "Elliptical HIIT",
      "Cardio Endurance",
      "Joint-Friendly Cardio",
      "Cross Training"
    ],
    "Stationary Bike": [
      "Bike Training",
      "Cycling Intervals",
      "Endurance Cycling",
      "Bike HIIT",
      "Recovery Ride",
      "Cardio Cycling"
    ],
    "Rowing Machine": [
      "Rowing Class",
      "Row Training",
      "Rowing Technique",
      "Full Body Row",
      "Rowing Intervals",
      "Endurance Row"
    ],
    "Stair Climber": [
      "Stair Climbing",
      "Step Training",
      "Climbing Intervals",
      "Cardio Climb",
      "Leg Endurance",
      "Vertical Training"
    ],
    "Jump Rope": [
      "Jump Rope",
      "Rope Training",
      "Cardio Rope",
      "Jump Rope HIIT",
      "Footwork Training",
      "Boxing Rope"
    ],
    "Running": [
      "Running Club",
      "Group Run",
      "Interval Training",
      "Distance Running",
      "Speed Training",
      "Marathon Training"
    ],
    "Indoor Cycling": [
      "Spin Class",
      "Cycling",
      "Bike Class",
      "Indoor Ride",
      "Cycle Training",
      "Rhythm Ride"
    ],
    "Swimming": [
      "Swim Training",
      "Lap Swimming",
      "Water Aerobics",
      "Swim Technique",
      "Masters Swimming",
      "Open Water Prep"
    ],
    "HIIT": [
      "HIIT",
      "High Intensity Training",
      "Interval Training",
      "Metabolic Training",
      "HIIT Circuit",
      "Tabata"
    ]
  },

  "Specialty Training": {
    "Rehabilitation Exercises": [
      "Physical Therapy",
      "Injury Recovery",
      "Corrective Exercise",
      "Movement Therapy",
      "Pain Management",
      "Functional Recovery"
    ],
    "Pre- and Post-Natal Fitness": [
      "Prenatal Fitness",
      "Postnatal Recovery",
      "Mom & Baby",
      "Pregnancy Yoga",
      "Postpartum Training",
      "Maternal Fitness"
    ],
    "Senior Fitness": [
      "Senior Fitness",
      "Active Aging",
      "Senior Strength",
      "Balance for Seniors",
      "Gentle Exercise",
      "Silver Sneakers"
    ],
    "Adaptive Fitness": [
      "Adaptive Training",
      "Inclusive Fitness",
      "Accessibility Training",
      "Modified Exercise",
      "Adaptive Sports",
      "Special Needs Fitness"
    ],
    "Tactical Training": [
      "Tactical Fitness",
      "Military Training",
      "First Responder Fitness",
      "Tactical Conditioning",
      "Law Enforcement Training",
      "Warrior Training"
    ]
  },

  "Personal Training": {
    "Personal Training": [
      "Personal Training",
      "1-on-1 Training",
      "Private Session",
      "Individual Training",
      "Custom Training",
      "Personal Coaching"
    ]
  },

  "Health Club": {
    "Health Club": [
      "General Fitness",
      "Open Gym",
      "Facility Access",
      "Equipment Training",
      "Gym Orientation",
      "Fitness Assessment"
    ],
    "24/7 Access": [
      "24/7 Access",
      "Anytime Fitness",
      "Open Access",
      "Self-Guided Training",
      "Independent Workout",
      "Flexible Access"
    ]
  },

  "Other": {
    "Other": [
      "Fitness Class",
      "Workout Session",
      "Training Session",
      "Group Exercise",
      "Specialty Class",
      "Custom Program"
    ]
  }
} as const;

// General class suggestions that work for most gym types as fallback
export const GENERAL_CLASS_SUGGESTIONS = [
  "Fitness Class",
  "Group Training",
  "Strength Training",
  "Cardio Workout",
  "HIIT Training",
  "Functional Fitness",
  "Core Training",
  "Flexibility & Stretching",
  "Personal Training",
  "Open Gym",
  "Beginner Fitness",
  "All Levels Training",
  "Conditioning Class",
  "Movement Training",
  "Wellness Session",
  "Recovery Session",
  "Team Training",
  "Fitness Challenge",
  "Health & Fitness",
  "Exercise Class"
] as const;

// Common appointment types that most gyms/fitness centers offer
export const APPOINTMENT_TYPE_SUGGESTIONS = [
  "Personal Training Session",
  "Fitness Assessment",
  "Body Composition Analysis",
  "Nutrition Consultation",
  "Goal Setting Session",
  "Injury Assessment",
  "Program Design Session",
  "Technique Coaching",
  "Strength Testing",
  "Mobility Assessment",
  "Recovery Session",
  "Wellness Consultation",
  "Member Check-in",
  "Progress Review",
  "Form Correction Session",
  "Specialized Training",
  "Physical Therapy",
  "Sports Massage",
  "Accountability Session",
  "Member Onboarding"
] as const;

export function getClassSuggestions(gymType: string, subType: string): string[] {
  const gymTypeData = CLASS_SUGGESTIONS[gymType as keyof typeof CLASS_SUGGESTIONS];

  if (!gymTypeData) {
    return [...GENERAL_CLASS_SUGGESTIONS];
  }

  const suggestions = gymTypeData[subType as keyof typeof gymTypeData];
  return suggestions ? [...suggestions] : [...GENERAL_CLASS_SUGGESTIONS];
}

export function getAppointmentTypeSuggestions(): string[] {
  return [...APPOINTMENT_TYPE_SUGGESTIONS];
}

export function getAppointmentTypeSuggestionsForGymType(gymType: string, subType: string): string[] {
  const gymTypeData = APPOINTMENT_TYPE_SUGGESTIONS_BY_GYM_TYPE[gymType as keyof typeof APPOINTMENT_TYPE_SUGGESTIONS_BY_GYM_TYPE];

  if (!gymTypeData) {
    return [...APPOINTMENT_TYPE_SUGGESTIONS];
  }

  const suggestions = gymTypeData[subType as keyof typeof gymTypeData];
  return suggestions ? [...suggestions] : [...APPOINTMENT_TYPE_SUGGESTIONS];
}

// Appointment type suggestions based on gym type and subtype
// Each subtype includes suggested appointment types that would be relevant for that type of gym
export const APPOINTMENT_TYPE_SUGGESTIONS_BY_GYM_TYPE = {
  "Functional Fitness": {
    "CrossFit": [
      "CrossFit Movement Screen",
      "WOD Performance Analysis",
      "Olympic Lifting Form Check",
      "Competition Prep Consultation",
      "Injury Prevention Assessment",
      "Scaling & Modification Session",
      "Goal Setting & Programming",
      "Benchmark WOD Assessment"
    ],
    "Circuit Training": [
      "Circuit Training Assessment",
      "Interval Training Consultation",
      "Metabolic Testing",
      "Exercise Form Review",
      "Progression Planning",
      "Recovery Strategy Session",
      "Heart Rate Zone Analysis"
    ],
    "Indoor Bootcamp": [
      "Bootcamp Fitness Assessment",
      "HIIT Readiness Evaluation",
      "Conditioning Consultation",
      "Modification Planning Session",
      "Injury Prevention Check",
      "Intensity Level Assessment"
    ],
    "Outdoor Bootcamp": [
      "Outdoor Fitness Assessment",
      "Weather Adaptation Planning",
      "Terrain Training Consultation",
      "Safety & Equipment Review",
      "Seasonal Training Planning"
    ],
    "Kettlebell Training": [
      "Kettlebell Movement Screen",
      "Swing Technique Analysis",
      "Kettlebell Program Design",
      "Injury Prevention Assessment",
      "Grip Strength Evaluation",
      "Flow Pattern Review"
    ],
    "TRX Training": [
      "TRX Movement Assessment",
      "Suspension Training Consultation",
      "Bodyweight Progression Planning",
      "Stability & Balance Evaluation",
      "Travel Workout Planning"
    ],
    "Battle Ropes": [
      "Battle Rope Technique Check",
      "Cardio Conditioning Assessment",
      "Interval Training Planning",
      "Shoulder Mobility Evaluation",
      "Power Output Analysis"
    ],
    "Plyometrics": [
      "Jump Mechanics Assessment",
      "Explosive Power Testing",
      "Landing Technique Review",
      "Injury Risk Evaluation",
      "Athletic Performance Analysis",
      "Progression Planning Session"
    ],
    "Calisthenics": [
      "Bodyweight Movement Screen",
      "Skill Progression Planning",
      "Strength Balance Assessment",
      "Flexibility Evaluation",
      "Goal Achievement Planning"
    ],
    "Olympic Weightlifting": [
      "Lifting Technique Analysis",
      "Mobility Assessment",
      "Competition Preparation",
      "Program Periodization",
      "Injury Prevention Screen",
      "Max Attempt Planning"
    ],
    "Strongman": [
      "Strongman Movement Assessment",
      "Event-Specific Coaching",
      "Injury Prevention Planning",
      "Competition Preparation",
      "Technique Refinement Session"
    ],
    "Ninja Warrior Training": [
      "Obstacle Course Assessment",
      "Grip Strength Evaluation",
      "Balance & Coordination Check",
      "Technique Coaching Session",
      "Competition Prep Planning"
    ],
    "Strength and Conditioning": [
      "Athletic Performance Assessment",
      "Sport-Specific Evaluation",
      "Strength Imbalance Analysis",
      "Conditioning Program Design",
      "Return-to-Sport Planning"
    ]
  },

  "Strength": {
    "Weightlifting": [
      "Strength Assessment",
      "Form Check Session",
      "Program Design Consultation",
      "Plateau Breaking Strategy",
      "Injury Prevention Screen",
      "Max Lift Attempt Planning",
      "Periodization Planning"
    ],
    "Powerlifting": [
      "Powerlifting Technique Analysis",
      "Meet Preparation Session",
      "Max Attempt Strategy",
      "Competition Planning",
      "Weakness Assessment",
      "Peaking Protocol Design"
    ],
    "Bodybuilding": [
      "Physique Assessment",
      "Contest Prep Consultation",
      "Muscle Imbalance Analysis",
      "Posing Instruction",
      "Nutrition Planning Session",
      "Progress Photo Review"
    ]
  },

  "Combat Sports": {
    "Boxing": [
      "Boxing Technique Assessment",
      "Sparring Readiness Evaluation",
      "Fight Preparation Session",
      "Defensive Skills Review",
      "Conditioning Assessment",
      "Mental Preparation Coaching"
    ],
    "Kickboxing": [
      "Kickboxing Technique Check",
      "Combination Flow Review",
      "Flexibility Assessment",
      "Conditioning Evaluation",
      "Self-Defense Consultation"
    ],
    "Muay Thai": [
      "Muay Thai Technique Analysis",
      "Clinch Work Assessment",
      "Thai Pad Training Session",
      "Conditioning Evaluation",
      "Cultural Training Discussion"
    ],
    "Brazilian Jiu-Jitsu": [
      "BJJ Technique Assessment",
      "Rolling Strategy Session",
      "Competition Preparation",
      "Injury Prevention Planning",
      "Belt Promotion Consultation"
    ],
    "Taekwondo": [
      "Taekwondo Technique Review",
      "Forms (Poomsae) Assessment",
      "Flexibility Evaluation",
      "Belt Testing Preparation",
      "Competition Strategy Session"
    ],
    "Karate": [
      "Karate Technique Assessment",
      "Kata Performance Review",
      "Self-Defense Evaluation",
      "Belt Advancement Planning",
      "Traditional Training Consultation"
    ],
    "Combat Sports": [
      "MMA Technique Assessment",
      "Cross-Training Planning",
      "Fight Preparation Session",
      "Injury Prevention Planning",
      "Mental Preparation Coaching"
    ]
  },

  "Group Fitness": {
    "Zumba": [
      "Dance Fitness Assessment",
      "Rhythm & Coordination Check",
      "Modification Consultation",
      "Injury Prevention Planning",
      "Class Preparation Session"
    ],
    "Spinning": [
      "Cycling Fitness Assessment",
      "Bike Fit Consultation",
      "Heart Rate Zone Planning",
      "Endurance Goal Setting",
      "Injury Prevention Check"
    ],
    "Barre": [
      "Barre Fitness Assessment",
      "Flexibility Evaluation",
      "Posture Analysis",
      "Modification Planning",
      "Injury Prevention Session"
    ],
    "Dance Classes": [
      "Dance Skill Assessment",
      "Choreography Learning Session",
      "Flexibility & Coordination Check",
      "Performance Preparation",
      "Injury Prevention Planning"
    ],
    "Group Circuit Training": [
      "Circuit Fitness Assessment",
      "Team Training Consultation",
      "Modification Planning Session",
      "Group Goal Setting",
      "Progress Evaluation"
    ],
    "Group Classes": [
      "Group Fitness Assessment",
      "Class Readiness Evaluation",
      "Modification Consultation",
      "Fitness Goal Setting",
      "Progress Check-in"
    ]
  },

  "Flexibility and Mobility": {
    "Yoga": [
      "Yoga Assessment",
      "Flexibility Evaluation",
      "Pose Modification Session",
      "Injury Prevention Planning",
      "Meditation Consultation",
      "Practice Goal Setting"
    ],
    "Pilates": [
      "Pilates Assessment",
      "Core Strength Evaluation",
      "Posture Analysis",
      "Injury Rehabilitation Planning",
      "Equipment Orientation",
      "Progress Evaluation"
    ],
    "Stretching": [
      "Flexibility Assessment",
      "Stretching Program Design",
      "Injury Prevention Planning",
      "Mobility Improvement Session",
      "Recovery Strategy Planning"
    ],
    "Foam Rolling": [
      "Myofascial Release Assessment",
      "Recovery Protocol Design",
      "Injury Prevention Planning",
      "Technique Instruction",
      "Self-Care Planning Session"
    ],
    "Tai Chi": [
      "Tai Chi Assessment",
      "Balance Evaluation",
      "Meditation Consultation",
      "Stress Management Session",
      "Mindful Movement Planning"
    ]
  },

  "Mind Body": {
    "Meditation": [
      "Meditation Assessment",
      "Mindfulness Consultation",
      "Stress Management Session",
      "Breathing Technique Review",
      "Mental Wellness Planning"
    ],
    "Mindful Breathing": [
      "Breathing Assessment",
      "Pranayama Instruction",
      "Stress Relief Planning",
      "Anxiety Management Session",
      "Wellness Consultation"
    ],
    "Relaxation Techniques": [
      "Stress Assessment",
      "Relaxation Strategy Planning",
      "Tension Relief Session",
      "Sleep Improvement Consultation",
      "Wellness Goal Setting"
    ],
    "Qi Gong": [
      "Qi Gong Assessment",
      "Energy Flow Evaluation",
      "Traditional Practice Consultation",
      "Healing Session Planning",
      "Mindful Movement Assessment"
    ],
    "Pranayama": [
      "Breathing Technique Assessment",
      "Yogic Breathing Instruction",
      "Energy Management Session",
      "Meditation Preparation",
      "Wellness Consultation"
    ],
    "Sauna": [
      "Heat Therapy Consultation",
      "Recovery Protocol Planning",
      "Wellness Assessment",
      "Detox Planning Session",
      "Health Screening"
    ]
  },

  "Outdoor Activities": {
    "Hiking": [
      "Hiking Fitness Assessment",
      "Trail Planning Session",
      "Gear Consultation",
      "Safety Planning Meeting",
      "Adventure Goal Setting"
    ],
    "Trail Running": [
      "Trail Running Assessment",
      "Terrain Training Planning",
      "Safety Consultation",
      "Gear Review Session",
      "Route Planning Meeting"
    ],
    "Outdoor Cycling": [
      "Cycling Fitness Assessment",
      "Bike Fit Consultation",
      "Route Planning Session",
      "Safety Planning Meeting",
      "Gear Review"
    ],
    "Kayaking": [
      "Water Skills Assessment",
      "Safety Training Session",
      "Equipment Orientation",
      "Trip Planning Meeting",
      "Technique Instruction"
    ],
    "Rock Climbing": [
      "Climbing Skills Assessment",
      "Safety Training Session",
      "Route Planning Consultation",
      "Equipment Review",
      "Risk Assessment Meeting"
    ],
    "Surfing": [
      "Surf Skills Assessment",
      "Ocean Safety Training",
      "Equipment Consultation",
      "Wave Reading Session",
      "Surf Forecast Planning"
    ],
    "Disc Golfing": [
      "Disc Golf Assessment",
      "Throwing Technique Review",
      "Course Strategy Session",
      "Equipment Consultation",
      "Tournament Preparation"
    ],
    "Stand-up Paddleboarding": [
      "SUP Skills Assessment",
      "Water Safety Training",
      "Balance Evaluation",
      "Equipment Orientation",
      "Trip Planning Session"
    ]
  },

  "Sports Specific Training": {
    "Basketball Training": [
      "Basketball Skills Assessment",
      "Performance Analysis",
      "Shooting Technique Review",
      "Conditioning Evaluation",
      "Season Planning Session"
    ],
    "Track & Field": [
      "Athletic Performance Assessment",
      "Technique Analysis",
      "Training Plan Development",
      "Competition Preparation",
      "Injury Prevention Planning"
    ],
    "Football Training": [
      "Football Performance Assessment",
      "Position-Specific Training",
      "Conditioning Evaluation",
      "Injury Prevention Planning",
      "Season Preparation"
    ],
    "Soccer Training": [
      "Soccer Skills Assessment",
      "Technical Analysis",
      "Conditioning Evaluation",
      "Tactical Planning Session",
      "Season Preparation"
    ],
    "Golf": [
      "Golf Fitness Assessment",
      "Swing Analysis",
      "Mobility Evaluation",
      "Performance Planning",
      "Injury Prevention Session"
    ],
    "Softball Training": [
      "Softball Skills Assessment",
      "Batting Analysis",
      "Conditioning Evaluation",
      "Season Planning",
      "Injury Prevention Planning"
    ],
    "Baseball Training": [
      "Baseball Skills Assessment",
      "Pitching Analysis",
      "Batting Technique Review",
      "Conditioning Evaluation",
      "Season Preparation"
    ],
    "Hockey Training": [
      "Hockey Performance Assessment",
      "Skating Analysis",
      "Off-Ice Conditioning Review",
      "Injury Prevention Planning",
      "Season Preparation"
    ],
    "Speed School": [
      "Speed Assessment",
      "Running Mechanics Analysis",
      "Agility Evaluation",
      "Performance Planning",
      "Athletic Development Session"
    ],
    "Sports Performance": [
      "Athletic Performance Assessment",
      "Sport-Specific Analysis",
      "Training Plan Development",
      "Competition Preparation",
      "Performance Optimization"
    ]
  },

  "Cardio": {
    "Treadmill": [
      "Running Assessment",
      "Gait Analysis",
      "Heart Rate Zone Planning",
      "Endurance Goal Setting",
      "Injury Prevention Check"
    ],
    "Elliptical": [
      "Low-Impact Cardio Assessment",
      "Joint Health Evaluation",
      "Endurance Planning",
      "Rehabilitation Consultation",
      "Fitness Goal Setting"
    ],
    "Stationary Bike": [
      "Cycling Assessment",
      "Bike Fit Consultation",
      "Endurance Planning",
      "Heart Rate Zone Analysis",
      "Injury Prevention Check"
    ],
    "Rowing Machine": [
      "Rowing Technique Assessment",
      "Full-Body Conditioning Review",
      "Endurance Planning",
      "Injury Prevention Check",
      "Performance Analysis"
    ],
    "Stair Climber": [
      "Climbing Assessment",
      "Lower Body Strength Review",
      "Endurance Planning",
      "Joint Health Evaluation",
      "Injury Prevention Check"
    ],
    "Jump Rope": [
      "Jump Rope Assessment",
      "Coordination Evaluation",
      "Cardio Planning Session",
      "Footwork Analysis",
      "Injury Prevention Check"
    ],
    "Running": [
      "Running Assessment",
      "Gait Analysis",
      "Training Plan Development",
      "Injury Prevention Planning",
      "Goal Setting Session"
    ],
    "Indoor Cycling": [
      "Cycling Assessment",
      "Bike Fit Consultation",
      "Endurance Goal Setting",
      "Heart Rate Zone Planning",
      "Performance Analysis"
    ],
    "Swimming": [
      "Swimming Assessment",
      "Stroke Technique Analysis",
      "Endurance Planning",
      "Water Safety Review",
      "Training Plan Development"
    ],
    "HIIT": [
      "HIIT Readiness Assessment",
      "Interval Training Planning",
      "Recovery Strategy Session",
      "Intensity Level Evaluation",
      "Injury Prevention Check"
    ]
  },

  "Specialty Training": {
    "Rehabilitation Exercises": [
      "Injury Assessment",
      "Recovery Planning Session",
      "Movement Analysis",
      "Pain Management Consultation",
      "Return-to-Activity Planning",
      "Corrective Exercise Design"
    ],
    "Pre- and Post-Natal Fitness": [
      "Prenatal Fitness Assessment",
      "Postnatal Recovery Planning",
      "Diastasis Recti Check",
      "Pelvic Floor Assessment",
      "Safe Exercise Consultation",
      "Postpartum Clearance Review"
    ],
    "Senior Fitness": [
      "Senior Fitness Assessment",
      "Fall Risk Evaluation",
      "Balance Assessment",
      "Medication Review Session",
      "Safe Exercise Planning",
      "Chronic Condition Consultation"
    ],
    "Adaptive Fitness": [
      "Adaptive Fitness Assessment",
      "Accommodation Planning",
      "Equipment Modification Session",
      "Accessibility Consultation",
      "Individualized Program Design",
      "Caregiver Education Session"
    ],
    "Tactical Training": [
      "Tactical Fitness Assessment",
      "Occupational Readiness Evaluation",
      "Injury Prevention Planning",
      "Performance Optimization",
      "Stress Management Session",
      "Equipment Training"
    ]
  },

  "Personal Training": {
    "Personal Training": [
      "Fitness Assessment",
      "Goal Setting Session",
      "Program Design Consultation",
      "Progress Review",
      "Technique Coaching",
      "Injury Prevention Planning",
      "Nutrition Consultation",
      "Accountability Check-in",
      "Body Composition Analysis",
      "Strength Testing",
      "Flexibility Assessment",
      "Lifestyle Coaching Session"
    ]
  },

  "Health Club": {
    "Health Club": [
      "Fitness Assessment",
      "Gym Orientation",
      "Equipment Training",
      "Goal Setting Session",
      "Workout Plan Design",
      "Progress Check-in",
      "Nutrition Consultation",
      "Injury Prevention Planning"
    ],
    "24/7 Access": [
      "Facility Orientation",
      "Safety Training",
      "Equipment Instruction",
      "Self-Guided Workout Planning",
      "Emergency Procedure Review",
      "Access Card Setup"
    ]
  },

  "Other": {
    "Other": [
      "Fitness Assessment",
      "Goal Setting Session",
      "Program Design Consultation",
      "Progress Review",
      "Injury Prevention Planning",
      "Wellness Consultation",
      "Technique Coaching",
      "Accountability Session"
    ]
  }
} as const;
